import React, { ChangeEvent, KeyboardEvent, useEffect, useState } from 'react';
import classNames from 'classnames';
import Icon from 'components/styleguide/icon/Icon';
import { useAutoSave } from 'components/styleguide/hooks/useAutoSave';

const styles = require('./Textarea.module.scss');

export interface TextareaProps {
  autoSaveUrl?: string;
  className?: string;
  context?: 'talent' | 'company' | 'neutral';
  defaultValidationMessage?: string;
  defaultValue?: string;
  disabled?: boolean;
  error?: string;
  hint?: string;
  id?: string;
  label?: string;
  maxLength?: number;
  name?: string;
  onChange?: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  required?: boolean;
  rows?: number;
  showCharacterCount?: boolean;
  textareaRef?: React.Ref<HTMLTextAreaElement>;
}

const Textarea: React.FC<TextareaProps> = ({
  autoSaveUrl,
  className,
  context = 'neutral',
  defaultValidationMessage,
  defaultValue,
  disabled,
  error,
  hint,
  id,
  label,
  maxLength,
  name,
  onChange,
  onKeyDown,
  placeholder,
  required,
  rows,
  showCharacterCount,
  textareaRef,
}) => {
  const { autoSaveError, save } = useAutoSave({
    url: autoSaveUrl || '',
    name: name || '',
  });
  const [length, setLength] = useState(defaultValue?.length || 0);
  const [displayError, setDisplayError] = useState(error || '');

  useEffect(() => { if (autoSaveError) setDisplayError(autoSaveError); }, [autoSaveError]);
  const textareaId = id || `textarea-${Math.random().toString(36).slice(2)}`;

  useEffect(() => {
    setDisplayError(error || '');
  }, [error]);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setLength(e.target.value.length);
    setDisplayError('');
    if (autoSaveUrl) save(e.target.value);
    onChange?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    if (required && (!e.target.value || e.target.value.trim() === '')) {
      setDisplayError(defaultValidationMessage);
    }
  };

  const hasMessage = Boolean(displayError) || Boolean(hint);
  const messageId = hasMessage ? `${textareaId}-message` : undefined;
  const countId = showCharacterCount && maxLength ? `${textareaId}-count` : undefined;
  const describedBy = [messageId, countId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={classNames(styles.container, className)}>
      {label && (
        <div className={styles.labelContainer}>
          <label htmlFor={textareaId} className={styles.label}>{label}</label>
          {required && <span className={styles.required} aria-hidden="true">*</span>}
        </div>
      )}
      <div className={classNames(styles.textareaWrapper, styles[context], { [styles.error]: Boolean(displayError), [styles.disabled]: disabled })}>
        <textarea
          aria-describedby={describedBy}
          aria-invalid={Boolean(displayError)}
          className={styles.textarea}
          defaultValue={defaultValue}
          disabled={disabled}
          id={textareaId}
          maxLength={maxLength}
          name={name}
          onBlur={handleBlur}
          onChange={handleChange}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          ref={textareaRef}
          required={required}
          rows={rows}
        />
        {showCharacterCount && maxLength && (
          <div id={countId} className={styles.characterCount} aria-live="polite">
            {length} of {maxLength} characters
          </div>
        )}
      </div>
      {hasMessage && (
        <div
          id={messageId}
          role={displayError ? 'alert' : undefined}
          className={classNames(styles.hintContainer, {
            [styles.hintError]: Boolean(displayError),
          })}
        >
          <Icon
            name={displayError ? 'triangleAlert' : 'info'}
            color={displayError ? 'error' : 'disable'}
            size="sm"
            aria-hidden="true"
          />
          <p className={styles.hintText}>{displayError || hint}</p>
        </div>
      )}
    </div>
  );
};

export default Textarea;
